import { createContext, useContext, useRef, useState } from "react";

import SourceSite from "@/shared/components/SourceSite";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { siteName, webAddress } from "@/shared/lib/sites";
import type { WebSource } from "@/shared/types";

/* A citation in an answer, `[2]` or `[1, 3]` (affect-ui's AI Answer): a chip naming the first
   source's site, "+1" for each more, and on hover, focus or a click a card per source with its
   title and snippet that opens it. The numbers are the ones the web tools gave the chat's
   sources (backend/agent/tools/web.py); a number the answer wasn't given stays the words it is,
   so a model can't draw a source it never read. */

/** The sources an answer may cite, by number. Empty outside a chat turn. */
export const CitationSources = createContext<ReadonlyMap<number, WebSource>>(new Map());

// a moment to move from the chip into its card before the card goes
const CLOSE_MS = 140;

export default function Citation({ ids, words }: { ids: number[]; words: string }) {
  const sources = useContext(CitationSources);
  const cited = ids.flatMap((n) => sources.get(n) ?? []);
  const [open, setOpen] = useState(false);
  const closing = useRef<number | undefined>(undefined);
  if (!cited.length) return <>{words}</>;
  const show = () => {
    window.clearTimeout(closing.current);
    setOpen(true);
  };
  const hide = () => {
    closing.current = window.setTimeout(() => setOpen(false), CLOSE_MS);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onPointerEnter={show}
          onPointerLeave={hide}
          aria-label={`${cited.length > 1 ? "Sources" : "Source"}: ${cited.map((source) => source.title).join(", ")}`}
          className="press inline-flex items-center gap-[3px] h-5 mx-[2px] px-[7px] rounded-[6px] align-[0.12em] bg-[var(--surface-2)] hover:bg-[var(--surface-3)] text-[11.5px] font-medium leading-none text-[var(--text-tertiary)] hover:text-[var(--text-primary)] data-[state=open]:bg-[var(--brand)] data-[state=open]:text-[var(--brand-foreground)]"
        >
          {siteName(cited[0].site)}
          {cited.length > 1 && <span className="opacity-70">+{cited.length - 1}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        sideOffset={6}
        onPointerEnter={show}
        onPointerLeave={hide}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-[300px] max-w-[calc(100vw-32px)] p-1.5 grid gap-0.5 rounded-[14px] border border-[var(--hairline-strong)] bg-[var(--surface)] shadow-menu"
      >
        {cited.map((source) => <SourcePreview key={source.n} source={source} />)}
      </PopoverContent>
    </Popover>
  );
}

function SourcePreview({ source }: { source: WebSource }) {
  const href = webAddress(source.url);
  const body = (
    <>
      <SourceSite source={source} />
      <span className="text-[13px] font-medium leading-[1.4] text-[var(--text-primary)]">{source.title}</span>
      {source.snippet && <span className="text-[12.5px] leading-[1.5] text-[var(--text-tertiary)] line-clamp-3">{source.snippet}</span>}
    </>
  );
  const box = "grid gap-1 px-2.5 pt-2 pb-2.5 rounded-[9px] min-w-0";
  return href ? (
    <a href={href} target="_blank" rel="noreferrer" className={`press ${box} hover:bg-[var(--surface-2)]`}>{body}</a>
  ) : (
    <span className={box}>{body}</span>
  );
}
